import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Loader2, ChevronDown, RefreshCw, ExternalLink, AlertTriangle,
  CheckCircle2, XCircle,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { alibabaApi } from "@/api/alibaba";
import { tasksApi } from "@/api/tasks";

function ProgressBar({ success, failed, total }: { success: number; failed: number; total: number }) {
  if (total === 0) return <div className="w-full h-2 bg-muted rounded-full" />;
  const sPct = (success / total) * 100;
  const fPct = (failed / total) * 100;
  return (
    <div className="w-full h-2 bg-muted rounded-full overflow-hidden flex">
      {sPct > 0 && <div className="h-full bg-green-500" style={{ width: `${sPct}%` }} />}
      {fPct > 0 && <div className="h-full bg-red-500" style={{ width: `${fPct}%` }} />}
    </div>
  );
}

function ModuleBar({ rate }: { rate: number }) {
  return (
    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${rate >= 80 ? "bg-green-500" : rate >= 50 ? "bg-yellow-500" : "bg-red-500"}`}
        style={{ width: `${rate}%` }}
      />
    </div>
  );
}

export function AlibabaCompleteness() {
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [failedOpen, setFailedOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: keywords = [], isLoading: kwLoading } = useQuery({
    queryKey: ["alibaba-completeness-keywords"],
    queryFn: async () => {
      const res = await alibabaApi.dataViewerKeywords();
      return res.data || [];
    },
  });

  const { data: diagnostics, isLoading: diagLoading } = useQuery({
    queryKey: ["alibaba-parse-diagnostics", selectedKeyword],
    queryFn: async () => {
      if (!selectedKeyword) return null;
      const res = await alibabaApi.parseDiagnostics(selectedKeyword);
      return res.data || null;
    },
    enabled: !!selectedKeyword,
  });

  const retryMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.retry(taskId),
    onSuccess: () => {
      toast.success("Re-crawl queued");
      queryClient.invalidateQueries({ queryKey: ["alibaba-parse-diagnostics"] });
    },
    onError: (err: Error) => toast.error(`Retry failed: ${err.message}`),
  });

  if (kwLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (keywords.length === 0) return null;

  const lowCoverageModules = diagnostics?.modules.filter(m => m.successRate < 50).length ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Product Insights Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead className="text-right w-20">Products</TableHead>
                <TableHead className="text-right w-20">Parsed</TableHead>
                <TableHead className="text-right w-16">OK</TableHead>
                <TableHead className="text-right w-16">Failed</TableHead>
                <TableHead className="text-right w-20">Rate</TableHead>
                <TableHead className="w-28">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keywords.map((kw) => {
                const parseRate = kw.profile_count > 0
                  ? Math.round((kw.parsed_ok / kw.profile_count) * 100)
                  : 0;
                const isSelected = selectedKeyword === kw.keyword;
                const bgClass = parseRate === 100 && kw.profile_count > 0
                  ? "bg-green-50 dark:bg-green-950/20"
                  : parseRate < 80 && kw.profile_count > 0
                    ? "bg-yellow-50 dark:bg-yellow-950/20"
                    : "";
                return (
                  <TableRow
                    key={kw.keyword}
                    className={`cursor-pointer hover:bg-muted/50 ${bgClass} ${isSelected ? "ring-1 ring-primary" : ""}`}
                    onClick={() => setSelectedKeyword(isSelected ? null : kw.keyword)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[200px]">{kw.keyword}</span>
                        {kw.parsed_failed > 0 && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{kw.product_count}</TableCell>
                    <TableCell className="text-right">{kw.profile_count}</TableCell>
                    <TableCell className="text-right text-green-600">{kw.parsed_ok}</TableCell>
                    <TableCell className="text-right text-red-500">{kw.parsed_failed > 0 ? kw.parsed_failed : "\u2014"}</TableCell>
                    <TableCell className="text-right font-medium">{kw.profile_count > 0 ? `${parseRate}%` : "\u2014"}</TableCell>
                    <TableCell><ProgressBar success={kw.parsed_ok} failed={kw.parsed_failed} total={kw.profile_count} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {selectedKeyword && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Module Diagnostics &mdash; {selectedKeyword}</h4>
              {diagLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>

            {diagnostics && !diagLoading && (
              <>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Module</TableHead>
                        <TableHead className="text-right w-16">OK</TableHead>
                        <TableHead className="text-right w-16">Failed</TableHead>
                        <TableHead className="text-right w-20">Rate</TableHead>
                        <TableHead className="w-28"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diagnostics.modules.map((m) => (
                        <TableRow key={m.module}>
                          <TableCell>
                            {m.successRate >= 80 ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                              : m.successRate >= 50 ? <AlertTriangle className="h-4 w-4 text-yellow-500" />
                              : <XCircle className="h-4 w-4 text-red-500" />}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-sm">{m.module}</span>
                            <span className="text-xs text-muted-foreground ml-2">{m.selector}</span>
                          </TableCell>
                          <TableCell className="text-right text-green-600">{m.success}</TableCell>
                          <TableCell className="text-right text-red-500">{m.failed > 0 ? m.failed : "\u2014"}</TableCell>
                          <TableCell className="text-right font-medium">{m.successRate}%</TableCell>
                          <TableCell><ModuleBar rate={m.successRate} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {lowCoverageModules > 0 && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    {lowCoverageModules}/7 modules have parse rate below 50%
                  </div>
                )}

                {diagnostics.failedProducts.length > 0 && (
                  <Collapsible open={failedOpen} onOpenChange={setFailedOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-2">
                        <ChevronDown className={`h-4 w-4 transition-transform ${failedOpen ? "rotate-180" : ""}`} />
                        Show {diagnostics.failedProducts.length} failed/partial products
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border rounded-lg overflow-hidden mt-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Product</TableHead>
                              <TableHead className="w-24">Status</TableHead>
                              <TableHead>Missing Modules</TableHead>
                              <TableHead className="w-20">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {diagnostics.failedProducts.map((p, i) => (
                              <TableRow key={p.profileId}>
                                <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                <TableCell>
                                  <div className="max-w-[250px]">
                                    <div className="text-sm font-medium truncate">{p.productName || "Unnamed"}</div>
                                    {p.productUrl && (
                                      <a href={p.productUrl} target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-primary hover:underline flex items-center gap-1">
                                        <ExternalLink className="h-3 w-3" /> View
                                      </a>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={p.parseStatus === "failed" ? "destructive" : "outline"}>{p.parseStatus}</Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {p.missingModules.map((mod) => (
                                      <Badge key={mod} variant="outline" className="text-[10px] h-4">{mod}</Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="sm" className="h-7 text-xs"
                                    disabled={retryMutation.isPending}
                                    onClick={(e) => { e.stopPropagation(); retryMutation.mutate(p.taskId); }}>
                                    <RefreshCw className="h-3 w-3 mr-1" /> Retry
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
